import React, { useCallback } from 'react';
import { Tooltip } from './ui/Tooltip';
import BookmarkIcon from './icons/BookmarkIcon';
import { useFollowUps } from '../contexts/FollowUpContext';
import type { FollowUpItemType } from '../services/followUps.service';

interface FollowUpBookmarkProps {
  type: FollowUpItemType;
  /** pronumber, `T`-prefixed template pro, `quoteNumber-revision`, or driver unit. */
  itemId: string;
  /** Snapshot text for the panel, e.g. `PRO 1234567 - ACME CORP`. */
  label: string;
  className?: string;
}

/**
 * The bookmark toggle in a detail view's header. Reads and writes
 * FollowUpContext and renders nothing else.
 *
 * `type="button"` and a stopped propagation are both load-bearing: this sits
 * inside the quote edit form and inside two drawer headers that take clicks of
 * their own, so a bare <button> would submit the form and a bubbling click
 * would also navigate or close the drawer.
 */
const FollowUpBookmark: React.FC<FollowUpBookmarkProps> = ({ type, itemId, label, className = '' }) => {
  const { isFollowed, follow, unfollow } = useFollowUps();

  const id = String(itemId ?? '').trim();
  const text = String(label ?? '').trim();
  const followed = id ? isFollowed(type, id) : false;

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (followed) void unfollow(type, id);
      else void follow(type, id, text);
    },
    [followed, follow, unfollow, type, id, text],
  );

  // Detail views mount before their record lands. A toggle with no id would
  // write a bookmark pointing nowhere, and one with no label would put a blank
  // row on the panel — so wait for both.
  if (!id || !text) return null;

  const action = followed ? 'Remove from Follow Ups' : 'Add to Follow Ups';

  return (
    <Tooltip content={action} placement="bottom">
      <button
        type="button"
        onClick={onClick}
        aria-label={action}
        aria-pressed={followed}
        className={`inline-flex shrink-0 items-center transition-colors ${
          followed
            ? 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
            : 'text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400'
        } ${className}`}
      >
        <BookmarkIcon filled={followed} className="w-4 h-4" />
      </button>
    </Tooltip>
  );
};

export default FollowUpBookmark;
